import { reportError } from "utils/diagnostics";
import React, { BaseSyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TableRow from "@mui/material/TableRow";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import Button from "@mui/material/Button";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Box, ListItemIcon, ListItemText, useTheme } from "@mui/material";

import { AxiosResponse } from "axios";
import { PageTitle } from "components/common/Text";
import { getUserList, UserListTypes } from "api/PowerUserHelpers";
import { convertCognitoAttr } from "utils/util-functions";
import { useAuth } from "hooks/useProvideAuth";
import { ButtonText } from "components/common/Buttons";
import { TableBodyCell } from "components/table/table.style";
import { AddRemoveOrgTypes, deleteUser, usersAddRemoveOrg } from "api/AdminHelpers";
import { showSuccess } from "utils/notifications";
import CustomTable from "components/table/table.component";
import NewAdminDialog from "components/power-user/new-admin-dialog.component";
import TableFilterNew from "components/table/TableFilterNew.component";
import useConfirm from "hooks/useConfirm";
import { fetchAllOrganizations } from "hooks/useOrganizations";
import ADMIN_COLUMNS, { AdminListTypes } from "./Admins.config";
import style from "./administration.module.css";
import { Popup } from "components/common/popup/popup.component";
import { CommonSelect } from "components/common/common-select/common-select.component";
import { FieldValues, useForm } from "react-hook-form";
import { OptionTypes, UserColumnTypes } from "pages/configs/configs";
import { Spinner } from "components/common/spinner/spinner.component";
import { OrgListDetailTypes } from "../Organizations/Organizations.config";

const AdminsPage = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isOrgUpdating, setIsOrgUpdating] = useState<boolean>(false);
  const [isRemoveOrgOpen, setIsRemoveOrgOpen] = useState<boolean>(false);
  const [updatingOrgText, setUpdatingOrgText] = useState<string>("");
  const [isAddOrgOpen, setIsAddOrgOpen] = useState<boolean>(false);
  const [isRemoveOrgDisabled, getIsRemoveOrgDisabled] = useState<boolean>(false);
  const [adminList, setAdminList] = useState<AdminListTypes[]>([]);
  const [isOpenNewAdminModal, setIsOpenNewAdminModal] = useState<boolean>(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [filterColumn, setFilterColumn] = useState<string>("");
  const [filterWord, setFilterWord] = useState<any>("");
  const [filterType, setFilterType] = useState<string>("");
  const [userOrgList, getUserOrgList] = useState<OptionTypes[]>([]);
  const [organizationList, setOrganizationList] = useState<OrgListDetailTypes[]>([]);
  const [areOrganizationsLoaded, setAreOrganizationsLoaded] = useState(false);
  const [totalAdminCount, setTotalAdminCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const requestId = useRef(0);
  const previousRefreshKey = useRef(refreshKey);

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState<AdminListTypes | null>();
  const open = Boolean(anchorEl);

  const filterColumns = ADMIN_COLUMNS.filter(({ filter }) => filter);

  const { sub: userId }: Record<string, string> = useAuth();
  const orgList = organizationList.map((group) => ({ value: group.GroupName, label: group.GroupName }));

  const theme = useTheme();
  const [getConfirmation, Confirmation] = useConfirm();

  const adminColumns = useMemo(() => {
    const tempCols = [...ADMIN_COLUMNS];
    tempCols.pop();

    return tempCols;
  }, []);

  const fetchAdmins = useCallback(async () => {
    const currentRequestId = requestId.current + 1;
    const refresh = previousRefreshKey.current !== refreshKey;
    requestId.current = currentRequestId;
    previousRefreshKey.current = refreshKey;
    setIsLoading(true);

    const body: UserListTypes = {
      username: userId,
      rolename: "admin",
      page,
      pageSize: rowsPerPage,
      refresh: refresh || undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      filterColumn: filterColumn || undefined,
      filterType: filterType || undefined,
      filterValue: filterWord || undefined,
      includeGroups: true,
    };

    try {
      const { data } = await getUserList(body);
      if (requestId.current !== currentRequestId) return;

      const users = (data?.Users ?? []).map((user: AdminListTypes & { Groups?: OrgListDetailTypes[] }) => ({
        ...user,
        ...convertCognitoAttr(user.Attributes),
        orgsList: user.Groups || [],
      }));
      setAdminList(users);
      setTotalAdminCount(data?.TotalCount || 0);
    } catch (error) {
      reportError("admins.AdminsPage", error);
      if (requestId.current === currentRequestId) {
        setAdminList([]);
        setTotalAdminCount(0);
      }
    } finally {
      if (requestId.current === currentRequestId) {
        setIsLoading(false);
      }
    }
  }, [filterColumn, filterType, filterWord, page, refreshKey, rowsPerPage, sortBy, sortOrder, userId]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const loadOrganizations = useCallback(async () => {
    if (areOrganizationsLoaded) return;

    const groups = await fetchAllOrganizations(userId);
    setOrganizationList(groups);
    setAreOrganizationsLoaded(true);
  }, [areOrganizationsLoaded, userId]);

  const refreshAdmins = useCallback(() => {
    setPage(0);
    setRefreshKey((value) => value + 1);
  }, []);

  const {
    formState: { errors },
    control,
    watch,
  }: FieldValues = useForm({ values: { groupName: orgList[0]?.value!, removeGroup: userOrgList[0]?.value! } });

  const groups = watch("groupName");
  const userGroups = watch("removeGroup");

  const onNewAdmin = async () => {
    setUpdatingOrgText("Loading Organizations...");
    setIsOrgUpdating(true);
    try {
      await loadOrganizations();
      setIsOpenNewAdminModal(true);
    } finally {
      setIsOrgUpdating(false);
    }
  };

  const getRowOrgList = (row: AdminListTypes) => {
    const newArr: OptionTypes[] = [];

    const list = Array.isArray(row?.orgsList) ? row!.orgsList : typeof row?.orgsList === "string" ? [row.orgsList] : [];

    list.forEach((item: any) => {
      const name = item && item.GroupName ? item.GroupName : item;
      const data: OptionTypes = { value: name, label: name };
      newArr.push(data);
    });

    getUserOrgList(newArr);
  };

  const onClickMenu = (event: BaseSyntheticEvent, row: AdminListTypes) => {
    setAnchorEl(event.currentTarget);
    setCurrentRow(row);
    getRowOrgList(row);

    if (!row.orgsList || row.orgsList.length < 2) {
      getIsRemoveOrgDisabled(true);
    } else {
      getIsRemoveOrgDisabled(false);
    }
  };

  const onMenuClose = () => {
    setAnchorEl(null);
    setCurrentRow(null);
  };

  const OnAddOrg = async () => {
    setAnchorEl(null);
    setUpdatingOrgText("Loading Organizations...");
    setIsOrgUpdating(true);
    try {
      await loadOrganizations();
      setIsAddOrgOpen(true);
    } finally {
      setIsOrgUpdating(false);
    }
  };

  const onRemoveOrg = () => {
    setAnchorEl(null);
    setIsRemoveOrgOpen(true);
  };

  const onDeleteAdmin = async () => {
    setAnchorEl(null);

    const isConfirm = await getConfirmation({
      title: "Delete Admin",
      content: "Are you sure that you want to delete the selected admin?",
      buttonTxt: ButtonText.Delete,
    });

    if (isConfirm) {
      try {
        const { data }: AxiosResponse<any, any> = await deleteUser({
          username: userId,
          userEmail: currentRow?.email,
        });

        showSuccess(data?.message);
        refreshAdmins();
      } catch (error) {
        reportError("pages/PowerUser/Admins/administration.page.onDeleteAdmin", error);
      }
    }
  };

  const handleRequestSort = (order: string, orderBy: string) => {
    setSortOrder(order);
    setSortBy(orderBy);
    setPage(0);
  };

  const onSearch = (column: string, columnType: string, searchContent: any) => {
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    setPage(0);
  };

  const onResetFilter = () => {
    setFilterColumn("");
    setFilterWord("");
    setFilterType("");
    setPage(0);
  };

  const handlePageChange = (pageNum: number) => {
    setPage(pageNum);
  };

  const handleRowsPerPageChange = (rowPerPage: number) => {
    setPage(0);
    setRowsPerPage(rowPerPage);
  };

  const handleRemoveOrg = async () => {
    const data: AddRemoveOrgTypes = {
      username: userId,
      groupname: userGroups,
      email: currentRow?.email!,
      action: "remove",
    };
    setUpdatingOrgText("Removing Organization from User...");
    setIsOrgUpdating(true);
    setIsRemoveOrgOpen(false);
    try {
      await usersAddRemoveOrg(data);
      refreshAdmins();
    } catch (error: any) {
      reportError("pages/PowerUser/Admins/administration.page.handleRemoveOrg", error);
    } finally {
      setIsOrgUpdating(false);
    }
  };

  const handleSubmitOrg = async () => {
    const data: AddRemoveOrgTypes = {
      username: userId,
      groupname: groups,
      email: currentRow?.email!,
      action: "add",
    };

    setUpdatingOrgText("Adding Organization to User...");
    setIsOrgUpdating(true);
    setIsAddOrgOpen(false);
    try {
      await usersAddRemoveOrg(data);
      refreshAdmins();
    } catch (error: any) {
      reportError("pages/PowerUser/Admins/administration.page.handleSubmitOrg", error);
    } finally {
      setIsOrgUpdating(false);
    }
  };

  const getColumnValue = (row: AdminListTypes, column: UserColumnTypes): JSX.Element => {
    let value: any;

    if (column.id === "orgsList") {
      const list = Array.isArray(row.orgsList) ? row.orgsList : typeof row.orgsList === "string" ? [row.orgsList] : [];

      if (!list.length) return <TableBodyCell />;

      if (list.length === 1) {
        const data: any = list[0];
        const orgName = data && data.GroupName ? data.GroupName : data;

        return <TableBodyCell>{orgName}</TableBodyCell>;
      }

      value = list;

      return (
        <TableBodyCell key={column.id}>
          <ul style={{ paddingLeft: "16px" }}>
            {value.map((item: any) => {
              const orgName = item && item.GroupName ? item.GroupName : item;
              return (
                <li key={orgName} style={{ padding: 0 }}>
                  {orgName}
                </li>
              );
            })}
          </ul>
        </TableBodyCell>
      );
    }

    value = row[column.id as keyof AdminListTypes];

    return <TableBodyCell key={column.id}>{column.format ? column.format(value) : value}</TableBodyCell>;
  };

  return (
    <>
      <PageTitle>Administrators</PageTitle>

      <div className={style.barContainer}>
        <div className={style.filterContainer}>
          {totalAdminCount > 10 || filterWord ? (
            <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
          ) : (
            <div />
          )}
        </div>
        <div className={style.buttonOptions}>
          <NxtIconButton icon={<AddCircleOutlineIcon />} text="New Admin" onClick={onNewAdmin} />
        </div>
      </div>
      <CustomTable
        boxShadow
        rowsPerPageOptions={[10, 25, 100]}
        totalCount={totalAdminCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      >
        <CustomTable.Header columns={ADMIN_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={isLoading} deviceData={adminList}>
          {adminList &&
            adminList.map(
              (row: AdminListTypes) =>
                row && (
                  <TableRow key={row.Username}>
                    {adminColumns.map((column) => {
                      return getColumnValue(row, column);
                    })}
                    <TableBodyCell style={{ width: 30 }}>
                      <Button onClick={(e) => onClickMenu(e, row)} style={{ color: theme.palette.common.black }}>
                        <MoreVertIcon />
                      </Button>
                    </TableBodyCell>
                  </TableRow>
                ),
            )}
        </CustomTable.Body>
      </CustomTable>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={onDeleteAdmin}>
          <ListItemIcon>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        <MenuItem onClick={OnAddOrg}>
          <ListItemIcon>
            <AddOutlinedIcon />
          </ListItemIcon>
          <ListItemText>Add Org</ListItemText>
        </MenuItem>
        <MenuItem disabled={isRemoveOrgDisabled} onClick={onRemoveOrg}>
          <ListItemIcon>
            <RemoveCircleOutlineOutlinedIcon />
          </ListItemIcon>
          <ListItemText>Remove Org</ListItemText>
        </MenuItem>
      </Menu>

      <NewAdminDialog
        open={isOpenNewAdminModal}
        orgList={orgList}
        userId={userId}
        onClose={() => setIsOpenNewAdminModal(false)}
        onAdd={refreshAdmins}
      />

      <Popup
        open={isAddOrgOpen}
        title="Add Organization"
        secondaryBtn={{ text: "Cancel", onClick: () => setIsAddOrgOpen(false) }}
        primaryBtn={{ text: "Add", onClick: handleSubmitOrg }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <CommonSelect
            name="groupName"
            label="Group Name"
            options={orgList || []}
            control={control}
            rules={{ required: "Organization is required" }}
          />
        </Box>
      </Popup>

      <Popup
        open={isRemoveOrgOpen}
        title="Remove Organization"
        secondaryBtn={{ text: "Cancel", onClick: () => setIsRemoveOrgOpen(false) }}
        primaryBtn={{ text: "Remove", onClick: handleRemoveOrg }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <CommonSelect
            name="removeGroup"
            label="Group Name"
            options={userOrgList || []}
            control={control}
            rules={{ required: "Organization is required" }}
          />
        </Box>
      </Popup>

      <Popup title={updatingOrgText} open={isOrgUpdating}>
        <Spinner />
      </Popup>

      <Confirmation />
    </>
  );
};

export default AdminsPage;
